import { create } from 'zustand';
import { currencySettingsService, CurrencySettings } from '../services/currencySettingsService';

interface CurrencySettingsState {
  settings: CurrencySettings;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  hasLoadedFromAPI: boolean; // Track if we've loaded from API to prevent unnecessary reloads
  lastSavedAt: number | null; // Timestamp of last successful save to prevent stale reloads
  setSettings: (settings: Partial<CurrencySettings>) => Promise<void>;
  resetToDefault: () => Promise<void>;
  loadSettings: (force?: boolean) => Promise<void>;
}

const defaultSettings: CurrencySettings = {
  symbol: '₫',
  precision: 0,
  text: 'VND',
  position: 'after',
  thousandsSeparator: ',',
  decimalSeparator: '.',
};

export const useCurrencySettingsStore = create<CurrencySettingsState>()((set, get) => ({
  settings: defaultSettings,
  isLoading: false,
  isSaving: false,
  error: null,
  hasLoadedFromAPI: false,
  lastSavedAt: null,
  
  // Load settings from API
  loadSettings: async (force = false) => {
    const state = get();
    
    // Skip if already loading
    if (state.isLoading) {
      return;
    }
    
    // Don't reload if we just saved recently (within last 2 seconds) unless forced
    if (!force && state.lastSavedAt && Date.now() - state.lastSavedAt < 2000) {
      console.log('Skipping loadSettings - recent save detected');
      return;
    }
    
    set({ isLoading: true, error: null });
    try {
      const settings = await currencySettingsService.getSettings();
      set({ settings, isLoading: false, hasLoadedFromAPI: true });
    } catch (error: any) {
      console.error('Failed to load currency settings:', error);
      set({ 
        error: error.message || 'Failed to load settings',
        isLoading: false,
        hasLoadedFromAPI: false // Allow retry on failure
      });
      // Keep using current settings on error
    }
  },
  
  // Set settings and sync to API
  setSettings: async (newSettings: Partial<CurrencySettings>) => {
    const currentSettings = get().settings;
    const updatedSettings = { ...currentSettings, ...newSettings };
    
    // Optimistically update local state
    set({ settings: updatedSettings, isSaving: true, error: null });
    
    try {
      // Sync to API
      const savedSettings = await currencySettingsService.updateSettings(newSettings);
      
      // Use the saved settings from API
      set({ 
        settings: savedSettings, 
        isSaving: false, 
        hasLoadedFromAPI: true,
        lastSavedAt: Date.now()
      });
    } catch (error: any) {
      console.error('Failed to save currency settings:', error);
      // Revert to previous settings on error
      set({ 
        settings: currentSettings,
        error: error.message || 'Failed to save settings',
        isSaving: false 
      });
      throw error;
    }
  },
  
  resetToDefault: async () => {
    set({ isSaving: true, error: null });
    try {
      // Reset in API
      await currencySettingsService.updateSettings(defaultSettings);
      set({ settings: defaultSettings, isSaving: false, hasLoadedFromAPI: true, lastSavedAt: Date.now() });
    } catch (error: any) {
      console.error('Failed to reset currency settings:', error);
      set({ 
        error: error.message || 'Failed to reset settings',
        isSaving: false 
      });
      // Still update local state even if API fails
      set({ settings: defaultSettings });
    }
  },
}));

// Helper function to format currency
export const formatCurrency = (
  amount: number,
  settings?: Partial<CurrencySettings>
): string => {
  const currentSettings = settings || useCurrencySettingsStore.getState().settings;
  
  const formattedNumber = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: currentSettings.precision,
    maximumFractionDigits: currentSettings.precision,
  }).format(amount);

  const numberWithCustomSeparators = formattedNumber
    .replace(/,/g, currentSettings.thousandsSeparator || ',')
    .replace(/\./g, currentSettings.decimalSeparator || '.');

  return currentSettings.position === 'before'
    ? `${currentSettings.symbol}${numberWithCustomSeparators}`
    : `${numberWithCustomSeparators}${currentSettings.symbol}`;
};
